<template>
  <div
    v-if="ready && !hiding"
    :class="[{ 'octadesk-app-popup': isPopup }, currentUserAgent]"
    class="app-main"
  >
    <ButtonSvg />

    <Button v-if="visibleButton" :custom-class="classStories" />

    <ProactiveMessage v-if="proactiveMessageShowCondition" />

    <div
      v-if="settings"
      class="octadesk-app-window"
      :class="[
        `octadesk-app-window--${settings.position}`,
        {
          'octadesk-app-window-closed': !active,
          'octadesk-app-window-opened': active
        }
      ]"
    >
      <div v-if="showLoaderContainer" :class="$style['loader-container']">
        <WidgetLoader v-if="restoringSession" />

        <WidgetLoader v-else-if="isLoading" i18n-key-message="loading" />
      </div>

      <router-view v-else />
    </div>
  </div>
</template>

<script>
  import queryString from 'querystring-browser'
  import { getSessionChatKey } from '@/services/session'

  import { mapActions, mapGetters } from 'vuex'
  import { subDomain as ResolveSubDomain } from '@octadesk-tech/services'
  import OctaStorage from '@octadesk-tech/storage'
  import services from '@/services'
  import { services as octadeskServices } from '@octadesk-tech/services'
  import Button from '@/app/components/Button.vue'

  import ButtonSvg from '@/app/components/ButtonSvg.vue'

  import WidgetLoader from '@/app/components/WidgetLoader'

  import ProactiveMessage from '@/app/components/ProactiveMessage'

  import { userAgent } from '@/helpers'
  import { sendEventClose } from './app/conversation/services/chat'
  import { initBeaconClient } from './services/beacon-client'

  const WIDGET_STORAGE_CONTEXT_KEY = 'octa-widget-v1'

  export default {
    name: 'App',
    components: {
      ButtonSvg,
      Button,
      WidgetLoader,
      ProactiveMessage
    },
    data() {
      return {
        fluxId: undefined,
        currentUserAgent: '',
        isLoading: true
      }
    },
    computed: {
      ...mapGetters([
        'settings',
        'hiding',
        'active',
        'ready',
        'restoringSession',
        'visibleButton',
        'company',
        'isPopup',
        'showProactiveMsg',
        'subDomainStatus',
        'stories',
        'showStories',
        'storiesList',
        'vueperStoriesDisabled',
        'selectedStorie',
        'manualCloseProactiveMessage'
      ]),
      showLoaderContainer() {
        return this.restoringSession || this.isLoading
      },
      isLocalhost() {
        return (
          ['localhost', '127.0.0.1'].includes(window.location.hostname) ||
          window.location.port === '8081'
        )
      },
      proactiveMessageShowCondition() {
        return (
          this.showProactiveMsg &&
          !this.showStories &&
          !this.manualCloseProactiveMessage
        )
      },
      hasMedias() {
        return this.storiesListMedias && this.storiesListMedias.length > 0
      },
      storiesListMedias() {
        return (
          this.storiesList &&
          this.storiesList.length &&
          this.storiesList
            .map(({ stories }) => [...stories.medias])
            .reduce((acc, x) => acc.concat([...x]), [])
        )
      },
      classStories() {
        if (this.hasMedias && !this.active && !this.selectedStorie) {
          if (this.vueperStoriesDisabled) return 'stories closed'

          return 'stories'
        }

        return ''
      }
    },
    async mounted() {
      await this.handleChatInitiation()

      await this.setCulture()

      this.deviceClass()

      await initBeaconClient()

      window.addEventListener('beforeunload', this.handleBeforeUnload)
    },
    beforeDestroy() {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    },
    methods: {
      ...mapActions([
        'setSettings',
        'setCompany',
        'setEmbedId',
        'setQueryOptions',
        'setReady',
        'startChooseFluxInterval',
        'log',
        'setButtonVisible',
        'activeApp',
        'getCulture',
        'setWhitelabel',
        'delayedStopRestoring',
        'restoreSession',
        'externalLogin',
        'overrideSetCustomFields',
        'updateChatCustomFields'
      ]),
      handleBeforeUnload() {
        const roomKey = getSessionChatKey()

        const message = this.$t('closeRoomBeforeOnload')

        sendEventClose(roomKey, message)
      },
      deviceClass() {
        if (userAgent.isIOS()) {
          this.currentUserAgent = 'device-ios'

          return
        }

        if (userAgent.isAndroid()) {
          this.currentUserAgent = 'device-android'

          return
        }

        this.currentUserAgent = 'desktop'
      },
      async resolveClient() {
        await octadeskServices.createClient('widgetEngine')
      },
      checkWhitelabel(options) {
        const whitelabel = options.whiteLabel

        if (whitelabel) this.setWhitelabel(whitelabel === 'true')
      },
      safeParseJSON(jsonString) {
        if (
          typeof jsonString !== 'string' ||
          !jsonString ||
          jsonString === 'undefined' ||
          jsonString.trim() === ''
        ) {
          return { success: false, data: null }
        }

        try {
          const parsed = JSON.parse(jsonString)

          return { success: true, data: parsed }
        } catch (error) {
          console.error(error)

          return { success: false, data: null }
        }
      },
      login(options) {
        const loginResult = this.safeParseJSON(options.login)

        if (loginResult.success) {
          this.externalLogin(loginResult.data)
        }
      },
      conversationCustomFields(options) {
        const customFieldsResult = this.safeParseJSON(
          options.conversationCustomFields
        )

        if (customFieldsResult.success) {
          this.overrideSetCustomFields(customFieldsResult.data)

          this.updateChatCustomFields()
        }
      },
      async handleChatInitiation() {
        this.isLoading = true

        try {
          const options = queryString.parse(
            window.location.search.replace('?', '')
          )

          options.url = options.baseUrl ? options.baseUrl : options.url

          if (!options) {
            return
          }

          if (
            options.showButton !== 'true' &&
            options.showButton !== undefined
          ) {
            this.setButtonVisible(false)
          }

          if (
            !options.subdomain &&
            this.isLocalhost &&
            !this.$route.query.subdomain
          ) {
            await this.setCompany('qas319100-a32')
          } else {
            await this.setCompany(
              this.$route.query.subdomain || options.subdomain
            )
          }

          OctaStorage.setContext(
            `${WIDGET_STORAGE_CONTEXT_KEY}_${this.company}`
          )

          ResolveSubDomain.setSubDomain(this.company)

          await this.resolveClient()

          if (!this.company || (this.company && !this.company.length)) {
            console.error('OctaChat - subDomain is required')

            this.log(`SubDomain not provided`)
          }

          this.setQueryOptions(options)

          this.setEmbedId(options.id)

          this.checkWhitelabel(options)

          await services.widget.validate()

          services.widget.hit()

          await this.setSettings()

          if (!this.settings.isEnabled) {
            return
          }

          await this.startChooseFluxInterval()

          await this.restoreSession()

          await this.delayedStopRestoring()

          this.login(options)

          this.conversationCustomFields(options)

          if (this.isPopup) {
            this.activeApp()
          }

          this.setReady()
        } catch (err) {
          console.error('widget initialize error', err)
        } finally {
          this.isLoading = false
        }
      },
      async setCulture() {
        this.$i18n.locale = await this.getCulture()
      }
    }
  }
</script>

<style lang="scss" module>
  .loader-container {
    width: 380px;
    height: 100%;
  }
</style>

<style>
  body ::-webkit-scrollbar {
    width: 11px;
    height: 11px;
  }

  body ::-webkit-scrollbar-thumb {
    min-height: 36px;
    border: 2px solid transparent;
    border-top: 3px solid transparent;
    border-bottom: 3px solid transparent;
    background-clip: padding-box;
    border-radius: 7px;
    background-color: #c4c4c4;
  }

  body ::-webkit-scrollbar-track {
    background-color: #f5f5f5;
  }

  .octadesk-app-window-opened {
    max-height: calc(100% - 5rem);
    border-radius: 8px;
  }

  @media screen and (max-device-width: 767px) {
    .octadesk-app-window-opened {
      max-height: 100% !important;
    }
  }

  .clear-margin {
    margin: 0 !important;
  }

  .space-bottom-xs {
    margin-bottom: 4px !important;
  }

  .layout-relative {
    position: relative;
  }

  .d-inline-block {
    display: inline-block;
  }

  .no-decoration {
    text-decoration: none !important;
    font-size: 12px;
    font-weight: 400;
  }

  /* FLEXBOX */
  .d-flex {
    display: flex;
  }

  .align-items-center {
    align-items: center;
  }

  .justify-content-center {
    justify-content: center;
  }

  .video-fluid-16-9 {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    padding-top: 25px;
    height: 0;
  }

  .video-fluid-16-9 iframe,
  .video-fluid-16-9 object,
  .video-fluid-16-9 embed {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  @media (max-width: 767px) {
    div.app-main.device-ios input,
    div.app-main.device-ios textarea {
      font-size: 16px !important;
    }
  }
</style>
